'use client';

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MouseEvent
} from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import MDEditor, { commands as mdCommands } from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import './McpExportPdfAction.css';
import { FileDownIcon } from 'lucide-react';
import { useAuiState } from '@assistant-ui/react';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { useAppContext } from '@/Main';
import { useRenderSeoReportPdfMutation } from '../api/mcpPdfApi';
import { errorMessage, successMessage } from '@/utils/toast';
import { stripFollowups, stripHtmlBreaks } from '../utils/followupMarkers';
import {
  backgroundAnswerText,
  isSpeedPlaceholder,
  useBackgroundReports
} from './skill-flow/backgroundReportStore';

const RTL_SCRIPT_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
const LTR_SCRIPT_RE = /[A-Za-z]/g;

/** Prefer RTL (Persian app default); flip to LTR when Latin clearly dominates. */
function detectContentDir(text: string): 'rtl' | 'ltr' {
  const sample = text.slice(0, 3000);
  const rtl = (sample.match(RTL_SCRIPT_RE) || []).length;
  const ltr = (sample.match(LTR_SCRIPT_RE) || []).length;
  if (rtl === 0 && ltr === 0) return 'rtl';
  return rtl >= ltr ? 'rtl' : 'ltr';
}

function extractAssistantMarkdown(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  const parts: string[] = [];
  for (const part of content) {
    if (
      part &&
      typeof part === 'object' &&
      'type' in part &&
      (part as { type?: string }).type === 'text' &&
      'text' in part
    ) {
      const text = String((part as { text?: unknown }).text ?? '');
      if (text.trim()) parts.push(text);
    }
  }
  return parts.join('\n\n');
}

function sanitizeFileName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
  if (!cleaned) return 'chat-export.pdf';
  return cleaned.toLowerCase().endsWith('.pdf') ? cleaned : `${cleaned}.pdf`;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** True when theme primary is light enough that white badge text would fail contrast. */
function isThemePrimaryLight(): boolean {
  if (typeof window === 'undefined') return false;
  const styles = getComputedStyle(document.documentElement);
  const rgb = styles.getPropertyValue('--falcon-primary-rgb').trim();
  if (rgb) {
    const parts = rgb.split(',').map(p => Number(p.trim()));
    if (parts.length >= 3 && parts.every(n => Number.isFinite(n))) {
      const [r, g, b] = parts;
      // Relative luminance (sRGB approx)
      const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return luma >= 0.55;
    }
  }
  const hex = styles.getPropertyValue('--falcon-primary').trim();
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luma >= 0.55;
}

/** Action-bar button + markdown edit modal → SEO report PDF download. */
export const McpExportPdfAction: FC = () => {
  const {
    config: { isDark, themeVariant }
  } = useAppContext() as {
    config: { isDark: boolean; themeVariant?: string };
  };
  const messageContent = useAuiState(s =>
    s.message.role === 'assistant' ? s.message.content : null
  );
  const backgroundReports = useBackgroundReports();
  const initialMarkdown = useMemo(() => {
    const fromText = stripHtmlBreaks(
      stripFollowups(extractAssistantMarkdown(messageContent))
    ).trim();
    const fromReport = backgroundAnswerText(
      messageContent,
      backgroundReports
    ).trim();
    if (isSpeedPlaceholder(fromText)) return fromReport || fromText;
    return fromText;
  }, [backgroundReports, messageContent]);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  // Null keeps the Bootstrap default (document.body). In fullscreen the chat
  // page is the top layer, so a body portal is not visible.
  const [fullscreenHost, setFullscreenHost] = useState<HTMLElement | null>(
    null
  );

  const syncFullscreenHost = () => {
    const page = anchorRef.current?.closest<HTMLElement>('.mcp-chat-page');
    const next =
      page &&
      (page.classList.contains('is-fullscreen') ||
        document.fullscreenElement === page)
        ? page
        : null;
    setFullscreenHost(prev => (prev === next ? prev : next));
  };

  useLayoutEffect(() => {
    syncFullscreenHost();
  });

  useEffect(() => {
    document.addEventListener('fullscreenchange', syncFullscreenHost);
    return () =>
      document.removeEventListener('fullscreenchange', syncFullscreenHost);
    // Mount listener only. syncFullscreenHost reads a ref and setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [title, setTitle] = useState('خروجی چت');
  const [lightPrimary, setLightPrimary] = useState(false);
  const [renderPdf, { isLoading }] = useRenderSeoReportPdfMutation();

  const contentDir = useMemo(() => detectContentDir(markdown), [markdown]);
  const colorMode = isDark ? 'dark' : 'light';

  useEffect(() => {
    if (open) {
      setMarkdown(initialMarkdown);
      setTitle('خروجی چت');
      setLightPrimary(isThemePrimaryLight());
    }
  }, [open, initialMarkdown, isDark, themeVariant]);

  const canExport = markdown.trim().length > 0 && !isLoading;

  const handleExport = async () => {
    const text = markdown.trim();
    if (!text) {
      errorMessage('متن برای ساخت PDF خالی است.');
      return;
    }
    try {
      const blob = await renderPdf({
        analyze_markdown: text,
        title: title.trim() || 'خروجی چت'
      }).unwrap();
      if (!(blob instanceof Blob) || blob.size === 0) {
        errorMessage('فایل PDF خالی برگشت. لطفاً دوباره تلاش کنید.');
        return;
      }
      // API may return JSON error with 200 in some gateways — sniff type.
      if (blob.type.includes('json')) {
        const textBody = await blob.text();
        let msg = 'ساخت PDF ناموفق بود.';
        try {
          const parsed = JSON.parse(textBody) as { msg?: string };
          if (parsed.msg) msg = parsed.msg;
        } catch {
          /* ignore */
        }
        errorMessage(msg);
        return;
      }
      triggerBlobDownload(blob, sanitizeFileName(title || 'خروجی چت'));
      successMessage('فایل PDF آماده شد.');
      setOpen(false);
    } catch {
      errorMessage('در ساخت یا دانلود PDF خطایی رخ داد.');
    }
  };

  if (!initialMarkdown) return null;

  return (
    <>
      <span ref={anchorRef} className="d-inline-flex">
        <TooltipIconButton
          tooltip="خروجی PDF"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <FileDownIcon className="size-3.5" />
        </TooltipIconButton>
      </span>

      <Modal
        show={open}
        container={fullscreenHost ?? undefined}
        onHide={() => {
          if (!isLoading) setOpen(false);
        }}
        size="xl"
        centered
        scrollable
        backdrop={isLoading ? 'static' : true}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <Modal.Header closeButton={!isLoading}>
          <Modal.Title className="fs-0">خروجی PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body dir="rtl">
          <Form.Group className="mb-3">
            <Form.Label className="fs--1 text-600">عنوان فایل</Form.Label>
            <Form.Control
              size="sm"
              value={title}
              disabled={isLoading}
              onChange={e => setTitle(e.target.value)}
              placeholder="مثلاً گزارش سئو"
            />
          </Form.Group>

          <div
            className={`mcp-pdf-md-editor mcp-pdf-md-editor--${contentDir}${
              lightPrimary ? ' mcp-pdf-md-editor--light-primary' : ''
            }`}
            data-color-mode={colorMode}
            dir="ltr"
          >
            <MDEditor
              value={markdown}
              onChange={value => setMarkdown(value ?? '')}
              height={480}
              minHeight={448}
              preview="live"
              highlightEnable={false}
              visibleDragbar={false}
              commands={mdCommands.getExtraCommands()}
              extraCommands={mdCommands.getCommands()}
              style={{
                height: 480,
                fontFamily: "dana, 'Open Sans', sans-serif"
              }}
              textareaProps={{
                disabled: isLoading,
                placeholder: 'متن Markdown را ویرایش کنید…',
                dir: contentDir,
                style: {
                  textAlign: contentDir === 'rtl' ? 'right' : 'left',
                  direction: contentDir,
                  fontFamily: "dana, 'Open Sans', sans-serif",
                  fontSize: 14,
                  lineHeight: '22px'
                }
              }}
              previewOptions={{
                style: {
                  fontFamily: "dana, 'Open Sans', sans-serif",
                  fontSize: 14,
                  direction: contentDir,
                  textAlign: contentDir === 'rtl' ? 'right' : 'left'
                }
              }}
            />
          </div>
          <div className="fs--2 text-500 mt-1" dir="rtl">
            سمت راست ویرایش، سمت چپ پیش‌نمایش؛ می‌توانید پیش‌نمایش را مخفی یا تمام‌صفحه کنید.
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between gap-2">
          <Button
            variant="falcon-default"
            size="sm"
            disabled={isLoading}
            onClick={() => setOpen(false)}
          >
            انصراف
          </Button>
          <Button
            variant="falcon-primary"
            size="sm"
            disabled={!canExport}
            onClick={() => {
              void handleExport();
            }}
            className="d-inline-flex align-items-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ width: 14, height: 14 }}
                />
                در حال ساخت PDF…
              </>
            ) : (
              <>
                <FileDownIcon size={14} />
                خروجی PDF
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
